import {
  type Viewer,
  type ViewerAnnotation,
  type ViewerEventMap,
  type ViewerInitOptions,
  type ViewerSceneOptions,
} from '@mindcloudxai/shared';
import { EventHandler, type Entity } from 'playcanvas';
import { createApp } from '../../submodules/supersplat-viewer/src/core/app';
import { observe } from '../../submodules/supersplat-viewer/src/core/observe';
import type { Global } from '../../submodules/supersplat-viewer/src/types';
import type { App } from '../../submodules/supersplat-viewer/src/app';
import { type SplatHandle, type SuperSplatRuntime, type ViewerContext } from './types';
import { createInitialState } from './state';
import { buildViewerGlobal, applyInitialCamera, loadSceneContent } from './scene-loader';
import { applyCanvasSize } from './canvas-resizer';
import { createTrackRouteDebug } from './track-route-debug';
import { bridgeViewerEvents } from './event-bridge';
import { createAvatarFallDetector, type AvatarFallDetector } from './avatar-fall';
import { initXrLifecycle } from './xr-lifecycle';
import { createCameraApi } from './api/camera';
import { createNavigationApi } from './api/navigation';
import { createSceneApi } from './api/scene';
import { createMeasureApi } from './api/measure';
import { createAvatarApi } from './api/avatar';
import { createInputApi } from './api/input';
import { createXrApi } from './api/xr';
import { createModelApi } from './api/model';

// ---------------------------------------------------------------------------
// SuperSplatViewer — @mindcloudxai/shared Viewer 契约的 PlayCanvas 实现。
//
// 由 supersplat-viewer 内部运行时（CameraManager / Picker / AnimController /
// Collision）闭环驱动；本层只做参数输入、只读查询、意图命令与事件桥接，
// 不在外部逐帧控制相机。原 fork 内 headless.ts 上提至此。
// ---------------------------------------------------------------------------

export class SuperSplatViewer implements Viewer {
  private canvas!: HTMLCanvasElement;
  private app!: App;
  private cameraEntity!: Entity;
  private global!: Global;
  private viewer?: SuperSplatRuntime;
  private events = new EventHandler();
  private initOptions: ViewerInitOptions = {};
  private splat: SplatHandle | null = null;
  private annotations: ViewerAnnotation[] = [];
  private disposeTrackRouteDebug: (() => void) | null = null;
  private pendingWalkJumps = 0;
  private disposed = false;
  private initialized = false;
  private lastWidth = 0;
  private lastHeight = 0;
  private avatarFallDetector!: AvatarFallDetector;

  readonly camera = createCameraApi(
    () => this.ctx(),
    () => this.assertInitialized(),
  );
  readonly navigation = createNavigationApi(
    () => this.ctx(),
    () => this.jumpWalk(),
  );
  readonly scene = createSceneApi(() => this.ctx());
  readonly measure = createMeasureApi(() => this.ctx());
  readonly avatar = createAvatarApi(() => this.ctx());
  readonly input = createInputApi(() => this.ctx());
  readonly xr = createXrApi({
    getApp: () => this.app,
    getCamera: () => this.cameraEntity,
    getGlobal: () => this.global,
    isDisposed: () => this.disposed,
    assertInitialized: () => this.assertInitialized(),
  });
  readonly model = createModelApi(
    () => this.ctx(),
    () => this.assertInitialized(),
  );

  // -------------------------------------------------------------------------
  // 生命周期
  // -------------------------------------------------------------------------

  async init(container: HTMLElement, options: ViewerInitOptions = {}): Promise<void> {
    this.initOptions = options;

    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    container.replaceChildren(canvas);
    this.canvas = canvas;

    const { app, camera, renderer } = await createApp(canvas, {
      renderer: options.renderer ?? 'webgpu',
    });
    this.app = app;
    this.cameraEntity = camera;

    const events = new EventHandler();
    const state = observe(events, createInitialState(options));
    this.global = buildViewerGlobal(app, camera, renderer, options, events, state);

    applyInitialCamera(camera, options);

    // 禁用引擎自动 resize，由调用方显式 resize() 控制尺寸
    // @ts-ignore
    app._allowResize = false;

    // avatar fall detector 需要在 global 就绪后绑定
    this.avatarFallDetector = createAvatarFallDetector({
      global: this.global,
      getViewer: () => this.viewer,
      events: this.events,
      resetNavigation: () => this.navigation.reset(),
    });

    initXrLifecycle({
      app,
      camera,
      canvas,
      global: this.global,
      getViewer: () => this.viewer,
      applyCanvasSize: () => this.applyCanvasSize(),
    });
    this.bridgeEvents();
    app.on('update', this.applyExternalInputFrame);
    app.on('update', this.avatarFallDetector.detect);
    app.start();
    this.initialized = true;
  }

  async loadScene(options: ViewerSceneOptions): Promise<void> {
    this.assertInitialized();

    // 替换旧场景，保证单场景闭环
    this.model._clearAll();
    this.scene.showTrackRoute?.(null);
    this.disposeTrackRouteDebug?.();
    this.disposeTrackRouteDebug = null;
    this.viewer?.destroy?.();
    this.viewer = undefined;
    this.splat?.dispose();
    this.splat = null;
    this.avatarFallDetector.reset();

    const result = await loadSceneContent(this.app, this.global, this.initOptions, options);
    this.viewer = result.viewer;
    this.splat = result.splat;
    this.annotations = result.annotations;
    this.disposeTrackRouteDebug = createTrackRouteDebug(this.app, options);
  }

  resize(width: number, height: number): void {
    if (!this.initialized || this.disposed) return;
    this.lastWidth = width;
    this.lastHeight = height;
    this.applyCanvasSize();
  }

  private applyCanvasSize(): void {
    applyCanvasSize(
      this.canvas,
      this.app,
      this.global,
      this.lastWidth,
      this.lastHeight,
      this.initialized,
      this.disposed,
    );
  }

  render(): void {
    if (!this.initialized || this.disposed) return;
    this.app.renderNextFrame = true;
    this.app.render();
  }

  flushFrame(deltaTime = 0): void {
    if (!this.initialized || this.disposed) return;
    this.app.update(deltaTime);
    this.app.renderNextFrame = true;
    this.app.render();
  }

  dispose(): void {
    if (this.disposed || !this.initialized) return;
    this.disposed = true;
    this.model._clearAll();
    this.scene.showTrackRoute?.(null);
    this.disposeTrackRouteDebug?.();
    this.disposeTrackRouteDebug = null;
    this.app.off('update', this.applyExternalInputFrame);
    this.app.off('update', this.avatarFallDetector.detect);
    this.viewer?.destroy?.();
    this.splat = null;
    this.viewer = undefined;
    this.app.destroy();
  }

  get currentSplatEntity(): Entity | null {
    return this.splat?.entity ?? null;
  }

  disposeCurrentSplat(): void {
    this.splat?.dispose();
    this.splat = null;
  }

  // -------------------------------------------------------------------------
  // 事件
  // -------------------------------------------------------------------------

  on<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void {
    this.events.on(event, cb);
  }

  off<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void {
    this.events.off(event, cb);
  }

  // -------------------------------------------------------------------------
  // 内部
  // -------------------------------------------------------------------------

  private ctx(): ViewerContext {
    return {
      global: this.global,
      viewer: this.viewer,
      canvas: this.canvas,
      camera: this.cameraEntity,
      app: this.app,
      annotations: this.annotations,
      events: this.events,
      disposed: this.disposed,
      initialized: this.initialized,
    };
  }

  private jumpWalk(): void {
    if (!this.global) return;
    this.pendingWalkJumps += 1;
    this.app.renderNextFrame = true;
  }

  private bridgeEvents(): void {
    bridgeViewerEvents({
      global: this.global,
      events: this.events,
      getAnimState: () => this.navigation.anim.getState(),
      getAvatarState: () =>
        this.avatar.getState() ?? {
          enabled: false,
          available: false,
          loading: false,
          url: '',
          status: '',
          animations: [],
          currentAnimation: '',
          followCamera: true,
        },
      applyCanvasSize: () => this.applyCanvasSize(),
    });
  }

  // walk/avatar 模式下将累积的跳跃意图注入 inputController frame
  private applyExternalInputFrame = (): void => {
    if (this.pendingWalkJumps <= 0 || !this.global || this.disposed) return;
    const mode = this.global.state.cameraMode;
    if (mode !== 'walk' && mode !== 'avatar') return;
    const frame = this.viewer?.inputController?.frame;
    if (!frame) return;
    frame.deltas.move.append([0, 1, 0]);
    this.pendingWalkJumps -= 1;
  };

  private assertInitialized(): void {
    if (!this.initialized || this.disposed) {
      throw new Error('SuperSplatViewer: not initialized or already disposed');
    }
  }
}

/** Factory：创建 PlayCanvas (SuperSplat) Viewer。 */
export function createViewer(): Viewer {
  return new SuperSplatViewer();
}
